<script setup lang="ts">
import MRateItem from '~/components/molecules/RateItem.vue'
import AButton from '~/components/atoms/buttons/Button.vue'
import ACheckbox from '~/components/atoms/forms/Checkbox.vue'
import RModalSignIn from '~/components/organs/modals/SignIn.vue'
import RModalSubscriptionByReview from '~/components/organs/modals/SubscriptionByReview.vue'
import { ButtonSize, ButtonStyle } from '~/static/enums/components/button'
import { openModal } from '~/utils/Modal'
import { VTooltip as vTooltip } from 'floating-vue'

interface Props {
  isAuth: boolean
  nameRate: string
  cost: number
  countMonths: number
  canBeObtainedByFree?: boolean
  downloads?: number
  generations?: number
  folderManagement?: boolean
  generationStorage?: boolean
  hasConfirmationCheckbox?: boolean
  oldPrice?: number
  descriptionBottom?: string
  paymentProcess?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'buy'): void
}>()

watch(() => {
  return props.cost + '-' + props.countMonths
}, () => {
  changeConfirmation(false)
})

const confirmation = ref(false)
const showConfirmationTooltip = ref(false)
let confirmationTooltipTimer: NodeJS.Timeout | null = null
const changeConfirmation = (value: boolean) => {
  confirmation.value = value
}
const checkConfirmation = () => {
  if (!confirmation.value && props.hasConfirmationCheckbox) {
    showConfirmationTooltip.value = true
    if (confirmationTooltipTimer !== null) {
      clearTimeout(confirmationTooltipTimer)
    }
    confirmationTooltipTimer = setTimeout(() => {
      showConfirmationTooltip.value = false
      confirmationTooltipTimer = null
    }, 3000)
    return false
  }
  return true
}

const buy = () => {
  if (props.isAuth) {
    if (!checkConfirmation()) {
      return
    }
    emit('buy')
  } else {
    openSignInModal()
  }
}

const openSignInModal = async () => {
  const modal = await openModal(RModalSignIn, {
    name: 'subscribeSignInModal',
    title: 'Войдите, чтобы оплатить',
  })
  modal.on('authorized', () => {
    emit('buy')
  })
}

const buttonText = computed(() => {
  if (props.oldPrice) {
    return 'Оплатить ' + (props.cost - props.oldPrice) + ' ₽'
  }
  if (props.countMonths === 1) {
    return 'Оплатить'
  }
  const cost = new Intl.NumberFormat('fr-FR', {
    maximumFractionDigits: 0,
  }).format(props.cost)
  return 'Оплатить ' + cost + ' ₽ за ' + (props.countMonths === 12 ? 'год' : props.countMonths + ' мес')
})

</script>

<template>
  <div class="col-12 col-md-5 col-xl-3 mb-3 mb-xl-0 position-relative">
    <MRateItem
      :name-rate="nameRate"
      :cost-by-month="cost / countMonths"
      :generations="generations"
      :folder-management="folderManagement"
      :generation-storage="generationStorage"
      :description-bottom="descriptionBottom"
    >
      <template #button>
        <div class="bottom-wrap">
          <div
            v-if="canBeObtainedByFree"
            class="link-unlimited-downloads"
          >
            <a
              href="#"
              @click.prevent="openModal(RModalSubscriptionByReview)"
            >
              Как получить бесплатно
            </a>
          </div>
          <AButton
            :button-style="ButtonStyle.Primary"
            :button-size="ButtonSize.Large"
            class="w-100 btn-rate"
            @click="buy"
          >
            <template v-if="paymentProcess">
              <div class="loading-area">
                <div
                  class="spinner-border"
                  role="status"
                />
              </div>
            </template>
            <template v-else>
              {{ buttonText }}
            </template>
          </AButton>
        </div>
      </template>
      <template #description>
        <ACheckbox
          v-if="hasConfirmationCheckbox"
          :id="'confirmation-' + cost + '-' + countMonths"
          :checked="confirmation"
          v-tooltip.bottom="{
            content: 'Поставьте галочку, чтобы оплатить подписку',
            shown: showConfirmationTooltip,
            triggers: [],
            distance: 6,
          }"
          @change="changeConfirmation"
          class="mt-2 text-start check-subscription"
        >
          Принимаю
          <a
            href="/legal/subscription-agreement.pdf"
            target="_blank"
            class="text-dark"
          >
            <span class="hover text-decoration-underline">
              соглашение о подписке
            </span>
          </a>
        </ACheckbox>
      </template>
    </MRateItem>
    <slot />
  </div>
</template>

<style lang="scss" scoped>
.loading-area {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 30px;
}

.spinner-border {
  --bs-spinner-border-width: 3px;
  --bs-spinner-width: 1.5rem;
  --bs-spinner-height: 1.5rem;
  color: white;
}

.btn-rate {
  border-radius: rem(4px);
  @include media-breakpoint-down(md) {
    font-size: rem(16px);
  }
}

.check-subscription {
  font-size: rem(14px);
  line-height: 1.5;
}

.bottom-wrap {
  margin: 0 rem(-1px) rem(-1px);

  .btn-rate {
    font-size: rem(16px);
    line-height: 1.5;
    font-weight: 400;
  }
}

.link-unlimited-downloads {
  text-align: center;
  position: absolute;
  width: 100%;
  margin-top: -1.5rem;
}
</style>
